import {
  Affix,
  Badge,
  Button,
  Descriptions,
  Drawer,
  Empty,
  List,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ApiOutlined,
  ClearOutlined,
  FileDoneOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import {
  clearApiRequestLogs,
  subscribeApiRequestLogs,
  type ApiRequestLog,
} from "@/utils/requestLogger";
import { pageApiProgressList, type PageApiProgress } from "./progress";

const { Paragraph, Text } = Typography;

const statusColorMap: Record<string, string> = {
  DONE: "success",
  PLACEHOLDER: "default",
  LEGACY: "warning",
  REAL: "success",
  MOCK: "processing",
  PARTIAL: "warning",
  NONE: "default",
};

const formatJson = (value: unknown) => {
  if (value === undefined) return "-";
  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return String(value);
  }
};

const AcceptanceToolkit = () => {
  const [progressOpen, setProgressOpen] = useState(false);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [logs, setLogs] = useState<ApiRequestLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<ApiRequestLog>();

  useEffect(() => subscribeApiRequestLogs(setLogs), []);

  const progressStats = useMemo(() => {
    const total = pageApiProgressList.length;
    const real = pageApiProgressList.filter((item) => item.apiStatus === "REAL").length;
    const partial = pageApiProgressList.filter((item) => item.apiStatus === "PARTIAL").length;
    const mock = pageApiProgressList.filter((item) => item.apiStatus === "MOCK").length;
    const none = pageApiProgressList.filter((item) => item.apiStatus === "NONE").length;
    return { total, real, partial, mock, none };
  }, []);

  const progressColumns: ColumnsType<PageApiProgress> = [
    {
      title: "模块",
      dataIndex: "module",
      width: 110,
      fixed: "left",
    },
    {
      title: "页面",
      dataIndex: "page",
      width: 150,
      fixed: "left",
    },
    {
      title: "路由",
      dataIndex: "route",
      width: 260,
      render: (value: string) => <Text code>{value}</Text>,
    },
    {
      title: "页面状态",
      dataIndex: "uiStatus",
      width: 120,
      render: (value: string) => <Tag color={statusColorMap[value]}>{value}</Tag>,
    },
    {
      title: "接口状态",
      dataIndex: "apiStatus",
      width: 120,
      render: (value: string) => <Tag color={statusColorMap[value]}>{value}</Tag>,
    },
    {
      title: "接口地址",
      dataIndex: "endpoints",
      render: (endpoints: string[]) =>
        endpoints.length ? (
          <Space wrap size={[4, 4]}>
            {endpoints.map((endpoint) => (
              <Tag key={endpoint} color="blue">
                {endpoint}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">暂无</Text>
        ),
    },
    {
      title: "备注",
      dataIndex: "note",
      width: 260,
      render: (value?: string) => value || "-",
    },
  ];

  const logColumns: ColumnsType<ApiRequestLog> = [
    {
      title: "时间",
      dataIndex: "time",
      width: 90,
    },
    {
      title: "方法",
      dataIndex: "method",
      width: 80,
      render: (value: string) => <Tag color="geekblue">{value}</Tag>,
    },
    {
      title: "状态",
      dataIndex: "ok",
      width: 100,
      render: (_value, record) => (
        <Tag color={record.ok ? "success" : "error"}>
          {record.status || "-"} {record.ok ? "OK" : "ERR"}
        </Tag>
      ),
    },
    {
      title: "耗时",
      dataIndex: "duration",
      width: 90,
      render: (value?: number) => (value === undefined ? "-" : `${value}ms`),
    },
    {
      title: "接口",
      dataIndex: "url",
      ellipsis: true,
      render: (value: string) => <Text code>{value}</Text>,
    },
  ];

  return (
    <>
      <Affix offsetBottom={32} style={{ position: "fixed", right: 24, zIndex: 1000 }}>
        <Space direction="vertical" size={10}>
          <Tooltip title="页面接口完成情况" placement="left">
            <Badge count={progressStats.total} size="small" offset={[-2, 4]}>
              <Button
                type="primary"
                shape="circle"
                size="large"
                icon={<FileDoneOutlined />}
                onClick={() => setProgressOpen(true)}
              />
            </Badge>
          </Tooltip>
          <Tooltip title="接口响应控制台" placement="left">
            <Badge count={logs.length} size="small" offset={[-2, 4]}>
              <Button
                type="default"
                shape="circle"
                size="large"
                icon={<ApiOutlined />}
                onClick={() => setConsoleOpen(true)}
              />
            </Badge>
          </Tooltip>
        </Space>
      </Affix>

      <Drawer
        title="验收进度与接口对接情况"
        width="82vw"
        open={progressOpen}
        onClose={() => setProgressOpen(false)}
        destroyOnClose
      >
        <Space size={16} wrap style={{ marginBottom: 16 }}>
          <Statistic title="页面总数" value={progressStats.total} />
          <Statistic title="已对接接口" value={progressStats.real} />
          <Statistic title="部分对接" value={progressStats.partial} />
          <Statistic title="Mock 页面" value={progressStats.mock} />
          <Statistic title="未对接" value={progressStats.none} />
        </Space>
        <Table
          rowKey={(record) => record.route}
          columns={progressColumns}
          dataSource={pageApiProgressList}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 1200 }}
          bordered
          size="middle"
        />
      </Drawer>

      <Drawer
        title="接口响应控制台"
        width="86vw"
        open={consoleOpen}
        onClose={() => setConsoleOpen(false)}
        extra={
          <Button icon={<ClearOutlined />} onClick={clearApiRequestLogs}>
            清空
          </Button>
        }
        destroyOnClose
      >
        <Space align="start" size={16} style={{ width: "100%" }}>
          <Table
            rowKey={(record) => record.id}
            columns={logColumns}
            dataSource={logs}
            pagination={{ pageSize: 12 }}
            scroll={{ x: 800, y: 560 }}
            style={{ width: "56vw" }}
            size="small"
            onRow={(record) => ({
              onClick: () => setSelectedLog(record),
            })}
            locale={{ emptyText: <Empty description="暂无接口请求记录" /> }}
          />
          <List
            bordered
            style={{
              flex: 1,
              maxHeight: 650,
              overflow: "auto",
              background: "#08111f",
              color: "#d6e4ff",
            }}
            header={<Text style={{ color: "#d6e4ff" }}>请求详情</Text>}
          >
            {selectedLog ? (
              <List.Item>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Descriptions
                    column={1}
                    size="small"
                    labelStyle={{ color: "#91caff" }}
                    contentStyle={{ color: "#f0f5ff" }}
                    items={[
                      { key: "url", label: "URL", children: selectedLog.url },
                      { key: "method", label: "Method", children: selectedLog.method },
                      {
                        key: "status",
                        label: "Status",
                        children: `${selectedLog.status || "-"} ${
                          selectedLog.ok ? "OK" : "ERR"
                        }`,
                      },
                      {
                        key: "duration",
                        label: "Duration",
                        children:
                          selectedLog.duration === undefined
                            ? "-"
                            : `${selectedLog.duration}ms`,
                      },
                    ]}
                  />
                  <Paragraph style={{ color: "#d6e4ff", marginBottom: 0 }}>
                    Request
                  </Paragraph>
                  <pre style={{ whiteSpace: "pre-wrap", color: "#95de64" }}>
                    {formatJson(selectedLog.request)}
                  </pre>
                  <Paragraph style={{ color: "#d6e4ff", marginBottom: 0 }}>
                    Response
                  </Paragraph>
                  <pre style={{ whiteSpace: "pre-wrap", color: "#69c0ff" }}>
                    {formatJson(selectedLog.response || selectedLog.error)}
                  </pre>
                </Space>
              </List.Item>
            ) : (
              <List.Item>
                <Empty description="点击左侧请求查看返回数据" />
              </List.Item>
            )}
          </List>
        </Space>
      </Drawer>
    </>
  );
};

export default AcceptanceToolkit;
